import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { FaBolt, FaChartLine } from "react-icons/fa";
import { KPICard } from "../../../components/reportDashboard/reportSummary/KPICard";
import { Card } from "../../../components/reportDashboard/common/Card";
import { DateRangeFilter } from "../../../components/reportDashboard/reportSummary/DateRangeFilter";

export function TravX() {
  const kpiData = [
    {
      label: "TravX Revenue",
      value: "$432,120",
      trend: "up",
      change: "+19.5%",
      sparkline: [35, 38, 37, 41, 40, 44, 48, 51, 55, 59],
    },
    {
      label: "Total White-Label Websites Sold",
      value: "1,847",
      trend: "up",
      change: "+24.3%",
      sparkline: [145, 152, 149, 160, 157, 168, 176, 182, 191, 201],
    },
    {
      label: "Active White-Label Websites",
      value: "1,623",
      trend: "up",
      change: "+18.7%",
      sparkline: [128, 134, 131, 141, 138, 148, 155, 160, 168, 175],
    },
    {
      label: "New White-Label Activations",
      value: "156",
      trend: "up",
      change: "+32.5%",
      sparkline: [10, 11, 11, 12, 12, 13, 14, 15, 16, 17],
    },
    {
      label: "Deactivated White-Labels",
      value: "224",
      trend: "down",
      change: "-8.4%",
      sparkline: [20, 20, 19, 19, 18, 18, 17, 17, 16, 16],
    },
    {
      label: "Net Active White-Labels",
      value: "+1,399",
      trend: "up",
      change: "+22.1%",
      sparkline: [108, 114, 112, 122, 120, 130, 138, 143, 152, 159],
    },
    {
      label: "Active Users",
      value: "2,134",
      trend: "up",
      change: "+15.8%",
      sparkline: [18, 19, 19, 20, 20, 21, 22, 23, 24, 25],
    },
    {
      label: "Transactions",
      value: "8,456",
      trend: "up",
      change: "+22.3%",
      sparkline: [68, 72, 70, 76, 74, 80, 85, 89, 94, 98],
    },
    {
      label: "API Calls",
      value: "124.5K",
      trend: "up",
      change: "+28.4%",
      sparkline: [95, 102, 99, 108, 105, 115, 122, 128, 136, 145],
    },
  ];

  const revenueByFeature = [
    { name: "Real-time Search", value: 172848, percentage: 40 },
    { name: "Dynamic Pricing", value: 129636, percentage: 30 },
    { name: "Multi-GDS Access", value: 86424, percentage: 20 },
    { name: "Smart Booking", value: 43212, percentage: 10 },
  ];

  const usageByIntegration = [
    { integration: "Flight Search API", calls: 45678, revenue: 145230 },
    { integration: "Hotel Availability", calls: 34567, revenue: 109870 },
    { integration: "Multi-City Planner", calls: 23456, revenue: 74560 },
    { integration: "Price Comparison", calls: 18765, revenue: 59650 },
    { integration: "Booking Engine", calls: 12345, revenue: 39230 },
  ];

  const monthlyTrend = [
    { month: "Jan", revenue: 28000, apiCalls: 8200 },
    { month: "Feb", revenue: 31000, apiCalls: 9100 },
    { month: "Mar", revenue: 35000, apiCalls: 10300 },
    { month: "Apr", revenue: 33000, apiCalls: 9700 },
    { month: "May", revenue: 38000, apiCalls: 11200 },
    { month: "Jun", revenue: 41000, apiCalls: 12100 },
    { month: "Jul", revenue: 44000, apiCalls: 12900 },
    { month: "Aug", revenue: 42000, apiCalls: 12300 },
    { month: "Sep", revenue: 36000, apiCalls: 10600 },
    { month: "Oct", revenue: 40000, apiCalls: 11800 },
    { month: "Nov", revenue: 43000, apiCalls: 12600 },
    { month: "Dec", revenue: 59000, apiCalls: 17300 },
  ];

  const COLORS = ["#1E61DC", "#60A5FA", "#93C5FD", "#BFDBFE"];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-[#1E61DC]/10 flex items-center justify-center">
            <FaBolt className="w-6 h-6 text-[#1E61DC]" />
          </div>
          <div>
            <span className="text-[#1A1A1A] text-xl">
              TravX Revenue Dashboard
            </span>
            <p className="text-[#6B7280]">
              Advanced travel technology platform and API performance
            </p>
          </div>
        </div>
        <DateRangeFilter />
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {kpiData.map((kpi, index) => (
          <KPICard key={index} {...kpi} />
        ))}
      </div>

      {/* Revenue & API Usage Trend */}
      <Card className="p-6 bg-white rounded-lg shadow-sm border border-gray-100">
        <span className="mb-6 text-[#1A1A1A] text-lg">
          Monthly Revenue & API Usage
        </span>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={monthlyTrend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="month" stroke="#6B7280" />
            <YAxis yAxisId="left" stroke="#6B7280" />
            <YAxis yAxisId="right" orientation="right" stroke="#6B7280" />
            <Tooltip
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
              }}
            />
            <Legend />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="revenue"
              stroke="#1E61DC"
              strokeWidth={3}
              name="Revenue ($)"
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="apiCalls"
              stroke="#10B981"
              strokeWidth={3}
              name="API Calls (K)"
            />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Revenue by Feature & Usage by Integration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 bg-white rounded-lg shadow-sm border border-gray-100">
          <span className="mb-6 text-[#1A1A1A] text-lg">
            Revenue by Feature
          </span>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={revenueByFeature}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                fill="#8884d8"
                paddingAngle={3}
                dataKey="value"
                label={({ name, percentage }) => `${name} ${percentage}%`}
              >
                {revenueByFeature.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                }}
                formatter={(value) => `$${value.toLocaleString()}`}
              />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6 bg-white rounded-lg shadow-sm border border-gray-100">
          <span className="mb-6 text-[#1A1A1A] text-lg">
            Top Integrations by Revenue
          </span>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={usageByIntegration} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis type="number" stroke="#6B7280" />
              <YAxis
                dataKey="integration"
                type="category"
                width={140}
                stroke="#6B7280"
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                }}
                formatter={(value) => `$${value.toLocaleString()}`}
              />
              <Bar dataKey="revenue" fill="#1E61DC" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Integration Performance */}
      <Card className="p-6 bg-white rounded-lg shadow-sm border border-gray-100">
        <span className="mb-4 text-[#1A1A1A]">
          Integration Performance Metrics
        </span>
        <div className="space-y-4">
          {[
            {
              name: "Flight Search API",
              calls: 45678,
              revenue: 145230,
              latency: "245ms",
              uptime: "99.8%",
            },
            {
              name: "Hotel Availability",
              calls: 34567,
              revenue: 109870,
              latency: "312ms",
              uptime: "99.6%",
            },
            {
              name: "Multi-City Planner",
              calls: 23456,
              revenue: 74560,
              latency: "189ms",
              uptime: "99.9%",
            },
            {
              name: "Price Comparison",
              calls: 18765,
              revenue: 59650,
              latency: "456ms",
              uptime: "99.5%",
            },
            {
              name: "Booking Engine",
              calls: 12345,
              revenue: 39230,
              latency: "523ms",
              uptime: "99.7%",
            },
          ].map((integration, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-4 bg-[#F8F9FC] rounded-lg"
            >
              <div className="flex items-center gap-4 flex-1">
                <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center">
                  <FaBolt className="w-5 h-5 text-[#1E61DC]" />
                </div>
                <div>
                  <div className="text-[#1A1A1A]">{integration.name}</div>
                  <div className="text-[#6B7280]">
                    {integration.calls.toLocaleString()} calls •{" "}
                    {integration.latency} avg • {integration.uptime} uptime
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[#1A1A1A]">
                  ${integration.revenue.toLocaleString()}
                </div>
                <div className="flex items-center gap-1 text-green-600">
                  <FaChartLine className="w-4 h-4" />
                  <span>+{(Math.random() * 20 + 10).toFixed(1)}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
